import React, { useMemo, useState } from 'react'
import { compose } from 'redux'
import { lpForm } from 'lp-form'
import { Field, propTypes as formPropTypes } from 'redux-form'
import {
  CloudinaryFileInput,
  CloudinaryMultiFileInput,
  RemovableThumbnail,
  RemovableFile,
  CheckboxGroup,
} from 'components'
import { ButtonArea, SubmitButton, Input, Textarea } from 'lp-components'
import { CLOUD_RESIZE_PRESET } from 'config'
import {
  formatObjectsToIds,
  parseIdsToObjects,
  serializeOptions,
  convertCloudinarySrcUrlToFilename,
  useCommunity,
} from 'utils'
import * as Types from 'types'
import PropTypes from 'prop-types'
import { withProps } from 'recompose'

const propTypes = {
  ...formPropTypes,
  disciplines: PropTypes.arrayOf(Types.discipline),
  leadershipCharacteristics: PropTypes.array,
  onSubmit: PropTypes.func,
}

const APPLICATION_CONFIG_KEY = 'partnerApplication'
const FIELDS_CONFIG_KEY = 'fields'

const buildConstraints = (community) => {
  const constraints = {}
  const fields = community?.[APPLICATION_CONFIG_KEY]?.[FIELDS_CONFIG_KEY] || {}

  Object.keys(fields).forEach((fieldName) => {
    const fieldConfig = fields[fieldName]
    if (!fieldConfig.visible) return

    constraints[fieldName] = {
      presence: fieldConfig.required ? true : false,
    }

    if (fieldName === 'contactEmail') constraints[fieldName].email = true
    if (fieldName === 'contactPhoneNumber' && fieldConfig.required) {
      constraints[fieldName].format = {
        pattern: /^\(\d{3}\)\s\d{3}-\d{4}$/,
        message: 'must be in format (xxx) xxx-xxxx',
      }
    }
    if (fieldName === 'address') {
      // Allow an empty/whitespace-only value so a blank required field only
      // shows the presence ("can't be blank") error. validate.js's format
      // validator only skips `undefined`, so without the `^\s*$` alternative a
      // single space would fail both presence AND format.
      constraints[fieldName].format = {
        pattern:
          /^\s*$|^\d+\s+[\w\s.,]+,\s*[\w\s]+,\s*[A-Z]{2}\s+\d{5}(-\d{4})?$/,
        message:
          'must be a valid address (e.g., "123 Main St, Chicago, IL 60601")',
      }
    }
    if (fieldName === 'disciplines' && fieldConfig.required) {
      constraints[fieldName].length = {
        minimum: 1,
        message: '^Please select at least one option',
      }
    }
    if (fieldName === 'leadershipCharacteristics' && fieldConfig.required) {
      constraints[fieldName].length = {
        minimum: 1,
        message: '^Please select at least one option',
      }
    }
  })

  return constraints
}

const isFieldVisible = (community, fieldName) => {
  const fields = community?.[APPLICATION_CONFIG_KEY]?.[FIELDS_CONFIG_KEY] || {}
  return fields[fieldName]?.visible !== false
}

const isFieldRequired = (community, fieldName) => {
  const fields = community?.[APPLICATION_CONFIG_KEY]?.[FIELDS_CONFIG_KEY] || {}
  return fields[fieldName]?.required === true
}

const getFieldLabel = (community, fieldName, baseLabel) => {
  const required = isFieldRequired(community, fieldName)
  return required ? `${baseLabel} *` : baseLabel
}

const filterNoneDiscipline = (disciplines) => {
  if (!disciplines) return []
  return disciplines.filter(
    (discipline) =>
      String(
        discipline?.displayName || discipline?.name || ''
      ).toLowerCase() !== 'none'
  )
}

function ApplicationFormStep1({
  change,
  error,
  handleSubmit,
  submitting,
  disciplines,
  leadershipCharacteristics,
  onSubmit,
}) {
  const community = useCommunity()
  const [orgImage, setOrgImage] = useState(null)
  const initialValuesId = 'initialValuesId'
  const milliseconds = Date.now()
  const uploadSuffix = `${initialValuesId}/${milliseconds}/`

  return (
    <form
      className="application-form"
      onSubmit={onSubmit ? handleSubmit(onSubmit) : handleSubmit}
      noValidate
    >
      {error && (
        <p className="full-error-message" role="alert">
          {error}
        </p>
      )}

      {isFieldVisible(community, 'organizationName') && (
        <Field
          name="organizationName"
          type="text"
          label={getFieldLabel(
            community,
            'organizationName',
            'Full Legal Organization Name'
          )}
          component={Input}
          autoComplete="organization"
        />
      )}
      {isFieldVisible(community, 'dbaName') && (
        <div className="label-container">
          <label>
            DBA Name
            {isFieldRequired(community, 'dbaName') ? (
              <span className="application-field-required" aria-hidden="true">
                {' '}
                *
              </span>
            ) : null}
          </label>
          <span className="application-field-hint">
            DBA Name if applicable. DBAs are the common name for the
            organization. If the organization does not have a DBA, please list
            the legal full name again.
          </span>
          <Field
            name="dbaName"
            type="text"
            label={false}
            component={Input}
            autoComplete="off"
          />
        </div>
      )}
      {isFieldVisible(community, 'organizationMission') && (
        <Field
          name="organizationMission"
          label={getFieldLabel(
            community,
            'organizationMission',
            'Organization Mission'
          )}
          component={Textarea}
          rows={4}
        />
      )}
      {isFieldVisible(community, 'address') && (
        <div className="label-container">
          <label>
            Address
            {isFieldRequired(community, 'address') ? (
              <span className="application-field-required" aria-hidden="true">
                {' '}
                *
              </span>
            ) : null}
          </label>
          <span className="application-field-hint">
            Address must be typed with the following formatting #### Street
            Name, Sacramento, CA #####. No extra spaces or periods can be
            entered. Commas must be entered in correct place.
          </span>
          <Field
            name="address"
            type="text"
            label={false}
            placeholder="123 Main St, Sacramento, CA 95814"
            component={Input}
            autoComplete="street-address"
          />
        </div>
      )}

      {(isFieldVisible(community, 'contactFirstName') ||
        isFieldVisible(community, 'contactLastName')) && (
        <div className="grid-container col-2">
          {isFieldVisible(community, 'contactFirstName') && (
            <Field
              name="contactFirstName"
              type="text"
              label={getFieldLabel(
                community,
                'contactFirstName',
                'Contact First Name'
              )}
              component={Input}
              autoComplete="given-name"
            />
          )}
          {isFieldVisible(community, 'contactLastName') && (
            <Field
              name="contactLastName"
              type="text"
              label={getFieldLabel(
                community,
                'contactLastName',
                'Contact Last Name'
              )}
              component={Input}
              autoComplete="family-name"
            />
          )}
        </div>
      )}

      {isFieldVisible(community, 'contactEmail') && (
        <Field
          name="contactEmail"
          type="email"
          label={getFieldLabel(community, 'contactEmail', 'Contact Email')}
          component={Input}
          autoComplete="email"
        />
      )}
      {isFieldVisible(community, 'contactPhoneNumber') && (
        <div className="label-container">
          <label>
            Contact Phone Number
            {isFieldRequired(community, 'contactPhoneNumber') ? (
              <span className="application-field-required" aria-hidden="true">
                {' '}
                *
              </span>
            ) : null}
          </label>
          <span className="application-field-hint">
            Phone numbers must be entered in the following format: (###)
            ###-####. Do not leave empty spaces at the end of the phone number.
          </span>
          <Field
            name="contactPhoneNumber"
            type="tel"
            label={false}
            placeholder="(555) 555-5555"
            component={Input}
            autoComplete="tel"
          />
        </div>
      )}

      {isFieldVisible(community, 'disciplines') && (
        <div className="label-container">
          <label>
            Which arts disciplines are addressed by your school programs?
            {isFieldRequired(community, 'disciplines') ? (
              <span className="application-field-required" aria-hidden="true">
                {' '}
                *
              </span>
            ) : null}
          </label>
          <span className="application-field-hint">Select all that apply.</span>
          <div className="floating-checkboxes">
            <Field
              name="disciplines"
              label={false}
              component={CheckboxGroup}
              format={formatObjectsToIds('id')}
              parse={parseIdsToObjects('id')}
              options={serializeOptions(filterNoneDiscipline(disciplines), {
                key: 'displayName',
                value: 'id',
              })}
            />
          </div>
        </div>
      )}

      {isFieldVisible(community, 'organizationWebsite') && (
        <Field
          name="organizationWebsite"
          type="text"
          label={getFieldLabel(
            community,
            'organizationWebsite',
            'Organization Website'
          )}
          placeholder="https://"
          component={Input}
          autoComplete="url"
        />
      )}
      {isFieldVisible(community, 'leadershipCharacteristics') && (
        <div className="label-container">
          <label>
            Which of the following characteristics apply to you or your
            organization?
            {isFieldRequired(community, 'leadershipCharacteristics') ? (
              <span className="application-field-required" aria-hidden="true">
                {' '}
                *
              </span>
            ) : null}
          </label>
          <span className="application-field-hint">Select all that apply.</span>
          <div className="floating-checkboxes">
            <Field
              name="leadershipCharacteristics"
              label={false}
              component={CheckboxGroup}
              format={formatObjectsToIds('id')}
              parse={parseIdsToObjects('id')}
              options={serializeOptions(leadershipCharacteristics || [])}
            />
          </div>
        </div>
      )}
      {isFieldVisible(community, 'organizationPhoto') && (
        <div className="label-container application-upload-group">
          <label>
            Organization Logo
            {isFieldRequired(community, 'organizationPhoto') ? (
              <span className="application-field-required" aria-hidden="true">
                {' '}
                *
              </span>
            ) : null}
          </label>
          <span className="application-upload-hint">
            (Accepted formats include png, jpg and jpeg. Your file must be less
            than 10 MB and will be cropped to be a square when uploaded)
          </span>
          <Field
            name="organizationPhoto"
            label={false}
            accept=".png,.jpg,.jpeg"
            component={CloudinaryFileInput}
            uploadPath={`/organizations/logos/${uploadSuffix}`}
            uploadPreset={CLOUD_RESIZE_PRESET}
            onUploadSuccess={({ secureUrl }) => {
              const filename = convertCloudinarySrcUrlToFilename(secureUrl)
              setOrgImage({ src: secureUrl, filename })
            }}
            previewComponent={RemovableThumbnail}
            remove={() => {
              change('organizationPhoto', '')
              setOrgImage(null)
            }}
            image={orgImage}
            className="col-4"
          />
        </div>
      )}

      {isFieldVisible(community, 'spotlightVideo') && (
        <div className="label-container application-upload-group">
          <label>
            Spotlight Video
            {isFieldRequired(community, 'spotlightVideo') ? (
              <span className="application-field-required" aria-hidden="true">
                {' '}
                *
              </span>
            ) : null}
          </label>
          <span className="application-upload-hint">
            Upload a video that showcases your programming in action in school
            or classroom settings. (Accepted formats include mp4. Your file must
            be less than 500 MB). Upload photos and videos only, do not attempt
            to drag and drop media.
          </span>
          <Field
            name="spotlightVideo"
            label={false}
            accept=".mp4"
            component={CloudinaryFileInput}
            uploadPath={`/organizations/spotlight-video/${uploadSuffix}`}
            onUploadSuccess={() => {}}
            previewComponent={RemovableFile}
            remove={() => change('spotlightVideo', '')}
          />
        </div>
      )}

      {isFieldVisible(community, 'spotlightImages') && (
        <div className="label-container application-upload-group">
          <label>
            Spotlight Images
            {isFieldRequired(community, 'spotlightImages') ? (
              <span className="application-field-required" aria-hidden="true">
                {' '}
                *
              </span>
            ) : null}
          </label>
          <span className="application-upload-hint">
            Upload photos that showcase your programming in action. (Accepted
            formats include png, jpg and jpeg. Your file must be less than 10
            MB.)
          </span>
          <Field
            name="spotlightImages"
            label={false}
            component={CloudinaryMultiFileInput}
            uploadPath={`/organizations/spotlight-images/${uploadSuffix}`}
            uploadPreset={CLOUD_RESIZE_PRESET}
            accept=".png,.jpg,.jpeg"
            onUploadSuccess={() => {}}
          />
        </div>
      )}

      <ButtonArea>
        <SubmitButton
          className="full-width button-secondary"
          submitting={submitting}
        >
          Next
        </SubmitButton>
      </ButtonArea>
    </form>
  )
}

ApplicationFormStep1.propTypes = propTypes

const FormWithConstraints = compose(
  withProps(() => ({
    beforeSubmit: ({ disciplines, leadershipCharacteristics, ...values }) => ({
      ...values,
      disciplines: formatObjectsToIds('id', disciplines),
      leadershipCharacteristics: formatObjectsToIds(
        'id',
        leadershipCharacteristics
      ),
    }),
  })),
  lpForm({
    name: 'partner-application-step1',
    constraints: {},
    initialValues: { spotlightImages: [] },
  })
)(ApplicationFormStep1)

function ApplicationFormStep1WithConfig(props) {
  const community = useCommunity()
  const constraints = useMemo(() => buildConstraints(community), [community])
  return <FormWithConstraints constraints={constraints} {...props} />
}

export default ApplicationFormStep1WithConfig
